import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { PropsOf } from 'app/@types/utils/props-of';
import usePageMeta from 'hooks/use-page-meta';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import RedirectLink from 'pages/Search/components/RedirectLink';
import SearchInput from 'pages/Search/components/SearchInput';
import SectionHeading from 'pages/Search/components/SectionHeading';
import { ROUTES, WILDCARD } from 'pages/Search/constants';
import { formatVersion, normalizeName } from 'pages/Search/utils';
import { useVersionsCatalog } from 'queries/catalog/use-versions-catalog';
import Utils from 'utils/Utils';

import { useProductVersionsCatalogPayload } from './hooks/use-product-versions-catalog-payload';
import useStyles from './styles';

const ProductVersions: React.FC = () => {
    const { classes } = useStyles();

    const layoutRef = useRef<{ scrollTop: VoidFunction } | null>(null);
    const [payload, actions] = useProductVersionsCatalogPayload();
    const { data, isLoading } = useVersionsCatalog(payload);

    useEffect(() => {
        if (data && layoutRef.current) {
            layoutRef.current.scrollTop();
        }
    }, [data]);

    const hasMetaPagination = payload.page > 1;
    const productName = normalizeName(payload.product);
    const vendorName = normalizeName(payload.vendor);

    usePageMeta({
        title: hasMetaPagination
            ? `${productName} vulnerabilities by versions - Page ${payload.page} | Vulners.com`
            : `${productName} vulnerabilities by versions | Vulners.com`,
        description: hasMetaPagination
            ? `${productName} - Page ${payload.page}. Here you can find all recent ${productName} security vulnerabilities from different versions`
            : `Here you can find all recent ${productName} security vulnerabilities from different versions`
    });

    const getPaginationHref = useCallback((page: number) => actions.url({ page }), [actions]);

    const tableHeaders = useMemo(
        () => [
            { field: 'version' as const, label: 'Version', sort: true },
            { field: 'cves' as const, label: 'Vulnerabilities', sort: true, width: 120 }
        ],
        []
    );

    const tableData = useMemo(
        () =>
            data?.items.map(({ version, cves }, index) => {
                const common: Pick<PropsOf<typeof RedirectLink>, 'to' | 'active' | 'className'> = {
                    to: ROUTES.PRODUCT_CVES({
                        page: null,
                        vendor: payload.vendor,
                        product: payload.product,
                        score: payload.score,
                        year: payload.year,
                        month: payload.month,
                        version: formatVersion(version)
                    }),
                    active: cves > 0,
                    className: classes.link
                };

                return [
                    <RedirectLink key={`version-${index}`} label={version || WILDCARD} {...common} />,
                    <RedirectLink key={`vulnerabilities-${index}`} label={cves} {...common} />
                ];
            }) ?? [],
        [classes.link, data?.items, payload.month, payload.product, payload.score, payload.vendor, payload.year]
    );

    const handlePageChange = useCallback((page: number) => actions.set({ page }), [actions]);
    const handlePageSizeChange = useCallback((size: number) => actions.set({ size }), [actions]);

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <div className={classes.content}>
                    <SectionHeading
                        title={[vendorName, productName]}
                        total={data?.total ?? NaN}
                        breadcrumbs={[
                            { to: ROUTES.PRODUCTS(), label: 'Products' },
                            { to: ROUTES.VENDOR({ vendor: payload.vendor }), label: vendorName },
                            {
                                to: ROUTES.PRODUCT_VERSIONS({ vendor: payload.vendor, product: payload.product }),
                                label: productName
                            },
                            isNumber(payload.score)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          score: payload.score
                                      }),
                                      label: `CVSS Score ${payload.score} - ${payload.score + 1}`
                                  }
                                : null,
                            isNumber(payload.year)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          year: payload.year
                                      }),
                                      label: payload.year.toString()
                                  }
                                : null,
                            isNumber(payload.month)
                                ? {
                                      to: ROUTES.PRODUCT_VERSIONS({
                                          vendor: payload.vendor,
                                          product: payload.product,
                                          month: payload.month
                                      }),
                                      label: Utils.monthOfYear(payload.month - 1)
                                  }
                                : null
                        ]}
                    />
                    <SearchInput
                        value={payload.search}
                        placeholder="Search by version"
                        onChange={(value) => actions.set({ search: value ?? undefined })}
                    />
                    <DataTable
                        headers={tableHeaders}
                        data={tableData}
                        loading={isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        onSortingChange={(sort) => actions.set({ sort })}
                        page={payload.page}
                        pageSize={payload.size}
                        totalCount={data?.total}
                        sorting={payload.sort}
                    />
                </div>
            }
        />
    );
};

export default ProductVersions;
